import type { Answers, QuestionId } from '@/features/quiz';

export interface MarketingPreset {
  id: string;
  /** Translation key under home.hero.presets — caption shown under sample ranking. */
  labelKey: string;
  answers: Answers;
}

function ans(qId: QuestionId, optionIds: string[]): Answers[QuestionId] {
  return { questionId: qId, optionIds };
}

/**
 * Six demo answer-sets used to cycle the live ranking preview on the landing.
 * Picked to surface different top-5 lineups so the animation isn't a single
 * frozen ranking with light shuffling.
 */
export const MARKETING_PRESETS: MarketingPreset[] = [
  {
    id: 'alpine_eu',
    labelKey: 'home.hero.presets.alpine_eu',
    answers: {
      climate: ans('climate', ['alps']),
      passport: ans('passport', ['eu']),
      priorities: ans('priorities', ['nature', 'healthcare']),
      income: ans('income', ['remote']),
      family: ans('family', ['partner']),
    },
  },
  {
    id: 'tropical_dn',
    labelKey: 'home.hero.presets.tropical_dn',
    answers: {
      climate: ans('climate', ['tropical']),
      passport: ans('passport', ['anglo']),
      priorities: ans('priorities', ['transport', 'culture']),
      income: ans('income', ['remote']),
      family: ans('family', ['solo']),
    },
  },
  {
    id: 'med_coast',
    labelKey: 'home.hero.presets.med_coast',
    answers: {
      climate: ans('climate', ['mediterranean']),
      passport: ans('passport', ['anglo']),
      urban_density: ans('urban_density', ['coastal']),
      priorities: ans('priorities', ['schools', 'safety']),
      family: ans('family', ['kids-school']),
      income: ans('income', ['remote']),
    },
  },
  {
    id: 'northern_jobs',
    labelKey: 'home.hero.presets.northern_jobs',
    answers: {
      climate: ans('climate', ['northern', 'continental']),
      passport: ans('passport', ['eu']),
      urban_density: ans('urban_density', ['capital']),
      priorities: ans('priorities', ['transport', 'healthcare']),
      income: ans('income', ['local']),
      family: ans('family', ['partner']),
    },
  },
  {
    id: 'sun_cost',
    labelKey: 'home.hero.presets.sun_cost',
    answers: {
      climate: ans('climate', ['mediterranean', 'tropical']),
      passport: ans('passport', ['other']),
      priorities: ans('priorities', ['safety']),
      income: ans('income', ['passive']),
      family: ans('family', ['solo']),
    },
  },
  {
    id: 'city_family',
    labelKey: 'home.hero.presets.city_family',
    answers: {
      climate: ans('climate', ['continental', 'mediterranean']),
      passport: ans('passport', ['anglo']),
      urban_density: ans('urban_density', ['capital']),
      priorities: ans('priorities', ['schools', 'healthcare']),
      family: ans('family', ['kids-preschool']),
      income: ans('income', ['business']),
    },
  },
];

/**
 * Single-dimension presets used by the "A taste, no signup" interactive
 * preview. Each one boosts a single dimension and leaves the rest default.
 */
export const TASTE_DIMENSIONS = [
  'climate',
  'cost',
  'visa',
  'healthcare',
  'safety',
  'tax',
] as const;
export type TasteDimension = (typeof TASTE_DIMENSIONS)[number];
